import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  message, 
  Space, 
  Typography, 
  Alert,
  Tag,
  Switch,
  Tooltip,
  Progress,
  Modal
} from 'antd';
import { 
  FolderOpenOutlined, 
  PlusOutlined, 
  ReloadOutlined,
  FileTextOutlined,
  StarFilled,
  StarOutlined,
  DownloadOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  RedoOutlined,
  FileExcelOutlined,
  ImportOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { handleFetchResponse, fetchWithProgress } from '../utils/authUtils';
import { API_ENDPOINTS } from '../utils/apiConfig';
// @ts-ignore
import tus, { Upload as TusUpload } from 'tus-js-client';
import { FolderUpload } from './FolderUpload';

const { Title, Text } = Typography;

// Add isDone and isReSolved to Task interface
interface Task {
  _id: string;
  name: string;
  category: string;
  creationDate: string;
  path?: string;
  isActive: boolean;
  isPriority?: boolean;
  assignedTo?: string | null;
  compressedFilePath?: string | null;
  compressionStatus?: 'pending' | 'compressing' | 'completed' | 'failed';
  isDone?: boolean;
  isReSolved?: boolean;
  pickedTime?: string;
  isDownloaded?: boolean;
  // Upload-related fields
  uploadStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  uploadedPath?: string;
  uploadedAt?: string;
  reSolvedUploadCount?: number; // Added for re-solved upload count
  /** Source URL for employee download (e.g. from Excel import) */
  dropboxDownloadLink?: string;
}

interface TaskResponse {
  data: Task[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  unassignedCount?: number; // Count of unassigned tasks (Admin/Leader only)
}

// This component implements the Task Management tab with role-based UI and logic.
// - Admin/Leader: can see all tasks, set/unset priority, and create tasks from folder.
// - Employee: can pick a random task, and only see their assigned tasks.

// --- Download management hook ---
function useTaskDownload() {
  const [downloadStatusMap, setDownloadStatusMap] = useState<{[taskId: string]: string}>({});
  const [downloadProgressMap, setDownloadProgressMap] = useState<{[taskId: string]: number}>({});
  const [activeDownload, setActiveDownload] = useState<{taskId: string, taskName: string, phase: string} | null>(null);

  // Reset download status/progress maps
  const resetDownloadMaps = () => {
    setDownloadStatusMap({});
    setDownloadProgressMap({});
    setActiveDownload(null);
  };

  // Reset specific task download state
  const resetTaskDownload = (taskId: string) => {
    setDownloadStatusMap((prev) => {
      const newMap = { ...prev };
      delete newMap[taskId];
      return newMap;
    });
    setDownloadProgressMap((prev) => {
      const newMap = { ...prev };
      delete newMap[taskId];
      return newMap;
    });
    if (activeDownload?.taskId === taskId) {
      setActiveDownload(null);
    }
  };

  const downloadFromSourceLink = async (taskId: string, taskName: string, downloadLink: string) => {
    setDownloadStatusMap((prev) => ({ ...prev, [taskId]: 'downloading' }));
    setDownloadProgressMap((prev) => ({ ...prev, [taskId]: 0 }));
    setActiveDownload({ taskId, taskName, phase: 'downloading' });
    const token = localStorage.getItem('token');
    
    try {
      if (!downloadLink) {
        throw new Error('No download link available for this task');
      }

      // Open source URL (browser download)
      // Create a temporary anchor element to trigger download
      const a = document.createElement('a');
      a.href = downloadLink;
      a.target = '_blank';
      a.download = `${taskName || taskId}.zip`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
      
      // Mark as successful after a short delay (browser handles the download)
      setTimeout(() => {
        setDownloadStatusMap((prev) => ({ ...prev, [taskId]: 'downloaded' }));
        setDownloadProgressMap((prev) => ({ ...prev, [taskId]: 100 }));
        message.success(`Download started for "${taskName}".`);
        
        // Confirm download
        fetch(API_ENDPOINTS.TASKS_DOWNLOAD_CONFIRM(taskId), {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        }).catch((confirmError) => {
          console.error('Failed to confirm download:', confirmError);
        });
        
        setActiveDownload(null);
      }, 1000);
      } catch (error) {
      console.error(`Error downloading for task ${taskId}:`, error);
        setDownloadStatusMap((prev) => ({ ...prev, [taskId]: 'error' }));
        setActiveDownload(null);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      message.error(`Failed to download: ${errorMessage}`);
    }
  };

  // Start download flow for a task
  const startDownload = async (taskId: string, taskName: string, downloadLink?: string) => {
    setActiveDownload({ taskId, taskName, phase: 'starting' });
    setDownloadStatusMap((prev) => ({ ...prev, [taskId]: 'starting' }));
    setDownloadProgressMap((prev) => ({ ...prev, [taskId]: 0 }));
    
    try {
      // If downloadLink is provided, use it directly
      if (downloadLink) {
        await downloadFromSourceLink(taskId, taskName, downloadLink);
        return;
      }

      // Otherwise, try to get task to get download link
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_ALL, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const tasks = await response.json();
        const task = tasks.find((t: any) => t._id === taskId);
        
        if (task && task.dropboxDownloadLink) {
          await downloadFromSourceLink(taskId, taskName, task.dropboxDownloadLink);
          return;
        }
      }
      
      throw new Error('Task not found or no download link available');
    } catch (e) {
      console.error(`Network error for task ${taskId}:`, e);
      setDownloadStatusMap((prev) => ({ ...prev, [taskId]: 'error' }));
        setActiveDownload(null);
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      message.error(`Download failed for "${taskName}": ${errorMessage}`);
    }
  };


  // Retry download
  const retryDownload = (taskId: string, taskName: string, downloadLink?: string) => {
    // Reset download state before retrying
    resetTaskDownload(taskId);
    // Start fresh download
    startDownload(taskId, taskName, downloadLink);
  };

  // Re-download for already downloaded tasks
  const reDownload = (taskId: string, taskName: string, downloadLink?: string) => {
    console.log(`🔄 Re-downloading task ${taskId}: ${taskName}`);
    // Reset download state and start fresh download
    resetTaskDownload(taskId);
    startDownload(taskId, taskName, downloadLink);
  };



  return {
    downloadStatusMap,
    downloadProgressMap,
    startDownload,
    retryDownload,
    reDownload,
    activeDownload,
    resetDownloadMaps,
    resetTaskDownload,
  };
}

const TaskTab: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  // Handle window resize for responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [pickingTask, setPickingTask] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const excelFileInputRef = useRef<HTMLInputElement | null>(null);
  const [importGoogleSheetVisible, setImportGoogleSheetVisible] = useState(false);
  const [importingGoogleSheet, setImportingGoogleSheet] = useState(false);
  const [googleSheetName, setGoogleSheetName] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [unassignedCount, setUnassignedCount] = useState<number>(0);

  const [uploadingTaskId, setUploadingTaskId] = useState<string | null>(null);
  const [uploadProgressMap, setUploadProgressMap] = useState<{[taskId: string]: number}>({});
  const [uploadStatusMap, setUploadStatusMap] = useState<{[taskId: string]: 'none'|'pending'|'completed'|'failed'}>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [currentUploadTask, setCurrentUploadTask] = useState<Task | null>(null);
  // --- Upload type state ---
  const [uploadType, setUploadType] = useState<'tus'|'stream'|'re-solved'>('tus');

  // --- Stream upload state ---
  const [streamUploadProgress, setStreamUploadProgress] = useState(0);
  const [streamUploadStatus, setStreamUploadStatus] = useState<'none'|'pending'|'completed'|'failed'>('none');

  const [downloadingTaskId, setDownloadingTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const downloadManager = useTaskDownload();

  // Check if current user has permission to create tasks
  const canCreateTasks = user?.role === 'Admin' || user?.role === 'Leader';
  const isEmployee = user?.role === 'Employee';
  
  // Get allowMultipleTasks from user context (set during login)
  const allowMultipleTasks = user?.allowMultipleTasks || false;
  
  // Check if employee has incomplete task (only disable if allowMultipleTasks is false)
  const hasIncompleteTask = isEmployee && !allowMultipleTasks && tasks.some(task => 
    task.assignedTo === user?.username && 
    task.isActive && 
    !task.isDone
  );

  /**
   * Fetch tasks from the API.
   * - For Employee: fetch only assigned tasks (GET /tasks/all, backend filters by assignedTo)
   * - For Admin/Leader: fetch all tasks (GET /tasks/all)
   */
  const fetchTasks = async (page: number = 1, pageSize: number = 10) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // All roles now use /tasks/all, backend filters for employees
      const url = `${API_ENDPOINTS.TASKS_ALL}?page=${page}&limit=${pageSize}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      await handleFetchResponse(
        response,
        (data: any) => {
          setTasks(data.data);
          setPagination({
            current: data.page,
            pageSize: data.limit,
            total: data.total,
          });
          // Set unassigned count for Admin/Leader roles
          if (data.unassignedCount !== undefined) {
            setUnassignedCount(data.unassignedCount);
          }
          // Reset download status/progress maps after tasks are updated
          downloadManager.resetDownloadMaps();
        },
        (data) => {
          message.error(data.message || 'Failed to load tasks');
        }
      );
    } catch (error) {
      console.error('Error fetching tasks:', error);
      message.error('An error occurred while loading tasks');
    } finally {
      setLoading(false);
    }
  };

  // Load tasks on component mount or when user changes
  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line
  }, [user]);

  // In TaskTab, listen for refreshTasks event to reload tasks
  useEffect(() => {
    const handler = () => fetchTasks();
    window.addEventListener('refreshTasks', handler);
    return () => window.removeEventListener('refreshTasks', handler);
  }, []);

  /**
   * Handle table pagination change (all roles)
   */
  const handleTableChange = (page: number, pageSize: number) => {
    fetchTasks(page, pageSize);
  };

  /**
   * Handle creating tasks from configured folder (Admin/Leader only)
   * Calls POST /tasks/create
   */
  const handleCreateTasks = async () => {
    setCreatingTasks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      await handleFetchResponse(
        response,
        (data) => {
          message.success(data.message || `Successfully created ${data.totalCreated} tasks`);
          fetchTasks(pagination.current, pagination.pageSize);
        },
        (data) => {
          message.error(data.message || 'Failed to create tasks');
        }
      );
    } catch (error) {
      console.error('Error creating tasks:', error);
      message.error('An error occurred while creating tasks');
    } finally {
      setCreatingTasks(false);
    }
  };

  /**
   * Handle Excel file selection and import
   */
  const handleExcelFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      message.error('Invalid file type. Please select an Excel file (.xlsx or .xls)');
      return;
    }

    await handleImportExcel(file);
    
    // Reset file input
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = '';
    }
  };

  /**
   * Import tasks from Excel file
   */
  const handleImportExcel = async (file: File) => {
    setImportingExcel(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(API_ENDPOINTS.TASKS_IMPORT_EXCEL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        // Show friendly summary (only success and errors, skip skipped count)
        const parts: string[] = [];
        if (data.success > 0) {
          parts.push(`✅ ${data.success} task${data.success > 1 ? 's' : ''} imported`);
        }
        if (data.failed > 0) {
          parts.push(`❌ ${data.failed} task${data.failed > 1 ? 's' : ''} failed`);
        }
        
        const summaryMsg = parts.length > 0 ? parts.join(', ') : 'Import completed';
        
        if (data.success > 0 && data.failed === 0) {
          message.success(summaryMsg, 5);
        } else if (data.failed > 0) {
          message.warning(summaryMsg, 5);
        } else {
          message.info(summaryMsg, 5);
        }

        // Show detailed errors if any
        const hasErrors = data.errors && data.errors.length > 0;
        
        if (hasErrors) {
          Modal.error({
            title: 'Import Errors',
            content: (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <Text strong style={{ color: '#ff4d4f' }}>Errors ({data.errors.length}):</Text>
                <div style={{ marginTop: '8px' }}>
                  {data.errors.map((err: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: '4px', fontSize: '12px', color: '#ff4d4f' }}>
                      Row {err.row}: {err.name && <strong>{err.name}</strong>} {err.message}
                    </div>
                  ))}
                </div>
              </div>
            ),
            width: 700,
            okText: 'OK',
          });
        }

        // Refresh task list
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        message.error(data.message || 'Failed to import tasks from Excel');
      }
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      message.error('An error occurred while importing Excel file');
    } finally {
      setImportingExcel(false);
    }
  };

  /**
   * Download Excel template file
   */
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_IMPORT_EXCEL_TEMPLATE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tasks-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success('Template downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading template:', error);
      message.error('Failed to download Excel template');
    }
  };

  /**
   * Import tasks from Google Sheets
   */
  const handleImportGoogleSheet = async () => {
    setImportingGoogleSheet(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_IMPORT_GOOGLE_SHEET, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sheetName: googleSheetName.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Show friendly summary (only success and errors, skip skipped count)
        const parts: string[] = [];
        if (data.success > 0) {
          parts.push(`✅ ${data.success} task${data.success > 1 ? 's' : ''} imported`);
        }
        if (data.failed > 0) {
          parts.push(`❌ ${data.failed} task${data.failed > 1 ? 's' : ''} failed`);
        }
        
        const summaryMsg = parts.length > 0 ? parts.join(', ') : 'Import completed';
        
        if (data.success > 0 && data.failed === 0) {
          message.success(summaryMsg, 5);
        } else if (data.failed > 0) {
          message.warning(summaryMsg, 5);
        } else {
          message.info(summaryMsg, 5);
        }

        // Show detailed errors if any
        const hasErrors = data.errors && data.errors.length > 0;
        
        if (hasErrors) {
          Modal.error({
            title: 'Import Errors',
            content: (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <Text strong style={{ color: '#ff4d4f' }}>Errors ({data.errors.length}):</Text>
                <div style={{ marginTop: '8px' }}>
                  {data.errors.map((err: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: '4px', fontSize: '12px', color: '#ff4d4f' }}>
                      Row {err.row}: {err.name && <strong>{err.name}</strong>} {err.message}
                    </div>
                  ))}
                </div>
              </div>
            ),
            width: 700,
            okText: 'OK',
          });
        }

        // Close modal and reset form
        setImportGoogleSheetVisible(false);
        setGoogleSheetName('');

        // Refresh task list
        fetchTasks(pagination.current, pagination.pageSize);
      } else {
        message.error(data.message || 'Failed to import tasks from Google Sheets');
      }
    } catch (error: any) {
      console.error('Error importing Google Sheet:', error);
      message.error('An error occurred while importing from Google Sheets');
    } finally {
      setImportingGoogleSheet(false);
    }
  };

  /**
   * Handle toggling priority for a task (Admin/Leader only)
   * Calls PUT /tasks/:id/set-priority
   */
  const handlePriorityToggle = async (taskId: string, isPriority: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_SET_PRIORITY(taskId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ isPriority }),
      });
      await handleFetchResponse(
        response,
        () => {
          message.success(`Task marked as ${isPriority ? 'Priority' : 'Ordinary'}`);
          fetchTasks(pagination.current, pagination.pageSize);
        },
        (data) => {
          message.error(data.message || 'Failed to update priority');
        }
      );
    } catch (error) {
      message.error('An error occurred while updating priority');
    }
  };

  const handleDeleteTask = async (taskId: string, taskName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_DELETE(taskId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      await handleFetchResponse(
        response,
        (data) => {
          const folderInfo = data.folderDeleted 
            ? ` and its folder (${data.folderPath})` 
            : '';
          message.success(`Task "${taskName}" deleted successfully${folderInfo}`);
          fetchTasks(pagination.current, pagination.pageSize);
        },
        (data) => {
          message.error(data.message || 'Failed to delete task');
        }
      );
    } catch (error) {
      console.error('Error deleting task:', error);
      message.error('An error occurred while deleting task');
    }
  };

  /**
   * Handle Employee picking a random task.
   * Calls POST /tasks/random-pick and shows notification.
   */
  const handlePickTask = async () => {
    // Prevent multiple simultaneous pick requests
    if (pickingTask) {
      message.warning('Please wait, picking task in progress...');
      return;
    }
    
    setPickingTask(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_RANDOM_PICK, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data && data.message) {
        // Check if user has incomplete task
        if (data.hasIncompleteTask) {
          message.warning(data.message, 5);
        } else {
        message.info(data.message);
        }
        return;
      }
      
      // If we get here, data is a task object
      message.success('Task picked successfully!');
      
      // Refresh task list
      await fetchTasks();
      
      // Auto-trigger download for the picked task using source link
      if (data && data._id && data.dropboxDownloadLink) {
        // Add small delay to ensure task list is updated
        setTimeout(() => {
          downloadManager.startDownload(data._id, data.name, data.dropboxDownloadLink);
        }, 500);
      } else if (data && data._id) {
        // If no download link, try to get it from task list
        setTimeout(() => {
          downloadManager.startDownload(data._id, data.name);
        }, 500);
      }
    } catch (error) {
      console.error('Error picking task:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      message.error(`Failed to pick task: ${errorMessage}`);
    } finally {
      setPickingTask(false);
    }
  };

  /**
   * Handle downloading a task file
   */
  const handleDownloadTask = async (taskId: string) => {
    setDownloadingTaskId(taskId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_DOWNLOAD_COMPRESSED(taskId), {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.status === 202) {
        // File chưa sẵn sàng, đang nén
        message.info('File is being compressed. Please wait...');
        // Polling lại sau 3s
        setTimeout(() => handleDownloadTask(taskId), 3000);
        return;
      }
      if (response.status === 404) {
        message.error('File not found on server.');
        return;
      }
      if (!response.ok) {
        message.error('Failed to download file.');
        return;
      }
      // File đã sẵn sàng, tải về
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      // Lấy tên file từ header
      const disposition = response.headers.get('content-disposition');
      let filename = 'task.zip';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '');
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingTaskId(null);
    }
  };

  // --- Start upload using tus-js-client ---
  const startTusUpload = () => {
    console.log('StartTusUpload called', fileInputRef.current?.files, uploadingTaskId);
    if (!fileInputRef.current?.files || !uploadingTaskId) {
      message.error('Bạn chưa chọn file hoặc task!');
      return;
    }
    const file = fileInputRef.current.files[0];
    if (!file) {
      message.error('Vui lòng chọn một file để tải lên.');
      return;
    }
    setUploadStatusMap((prev) => ({ ...prev, [uploadingTaskId]: 'pending' }));
    setUploadProgressMap((prev) => ({ ...prev, [uploadingTaskId]: 0 }));
    const token = localStorage.getItem('token');
    const upload = new TusUpload(file, {
      endpoint: '/api/upload/tus', // Backend tus endpoint
      headers: {
        Authorization: `Bearer ${token}`,
      },
      metadata: {
        filename: file.name,
        taskId: uploadingTaskId,
      },
      onError: (error: Error) => {
        setUploadStatusMap((prev) => ({ ...prev, [uploadingTaskId]: 'failed' }));
        message.error('Upload failed: ' + error.message);
        setUploadingTaskId(null);
        fetchTasks();
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        setUploadProgressMap((prev) => ({ ...prev, [uploadingTaskId]: Math.floor((bytesUploaded / bytesTotal) * 100) }));
        console.log(`Uploading: ${bytesUploaded}/${bytesTotal}`);
      },
      onSuccess: () => {
        setUploadStatusMap((prev) => ({ ...prev, [uploadingTaskId]: 'completed' }));
        message.success('Upload completed!');
        setUploadingTaskId(null);
        fetchTasks();
      },
    });
    upload.start();
  };

  // --- Pause upload ---
  const pauseUpload = () => {
    if (uploadingTaskId) {
      // This logic needs to be adapted if tusUpload is not directly accessible here
      // For now, we'll just show a message
      message.info('Resumable uploads are not directly supported in this UI.');
    }
  };

  // --- Resume upload ---
  const resumeUpload = () => {
    if (uploadingTaskId) {
      // This logic needs to be adapted if tusUpload is not directly accessible here
      // For now, we'll just show a message
      message.info('Resumable uploads are not directly supported in this UI.');
    }
  };

  // --- Stream upload handler ---
  const handleStreamUpload = async () => {
    if (!fileInputRef.current?.files || !uploadingTaskId) {
      message.error('Bạn chưa chọn file hoặc task!');
      return;
    }
    const file = fileInputRef.current.files[0];
    if (!file) {
      message.error('Vui lòng chọn một file để tải lên.');
      return;
    }
    setStreamUploadStatus('pending');
    setStreamUploadProgress(0);
    const token = localStorage.getItem('token');
    const url = `/api/tasks/upload-stream?taskId=${uploadingTaskId}&filename=${encodeURIComponent(file.name)}`;
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setStreamUploadProgress(Math.floor((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setStreamUploadStatus('completed');
          message.success('Stream upload completed!');
          setUploadingTaskId(null);
          fetchTasks();
        } else {
          setStreamUploadStatus('failed');
          message.error('Stream upload failed!');
        }
      };
      xhr.onerror = () => {
        setStreamUploadStatus('failed');
        message.error('Stream upload failed!');
      };
      xhr.send(file);
    } catch (err) {
      setStreamUploadStatus('failed');
      message.error('Stream upload failed!');
    }
  };

  // --- Mark as Done/Re-solved handlers ---
  const handleMarkAsDone = async (taskId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_SET_DONE(taskId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        message.success('Task marked as done!');
        fetchTasks();
      } else {
        message.error(data.message || 'Failed to mark as done');
      }
    } catch (error) {
      message.error('An error occurred while marking as done');
    }
  };

  const handleMarkAsReSolved = async (taskId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_SET_RE_SOLVED(taskId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        message.success('Task marked as re-solved!');
        fetchTasks();
      } else {
        message.error(data.message || 'Failed to mark as re-solved');
      }
    } catch (error) {
      message.error('An error occurred while marking as re-solved');
    }
  };

  // --- Toggle handlers for isDone and isReSolved ---
  const handleToggleIsDone = async (task: Task, checked: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_SET_DONE(task._id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: checked }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(`Task marked as ${checked ? 'done' : 'not done'}`);
        fetchTasks();
      } else {
        message.error(data.message || 'Failed to update status');
      }
    } catch (error) {
      message.error('An error occurred while updating status');
    }
  };

  const handleToggleIsReSolved = async (task: Task, checked: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TASKS_SET_RE_SOLVED(task._id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: checked }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(`Task marked as ${checked ? 're-solved' : 'not re-solved'}`);
        fetchTasks();
      } else {
        message.error(data.message || 'Failed to update status');
      }
    } catch (error) {
      message.error('An error occurred while updating status');
    }
  };

  // --- Handle file selection ---
  const handleFileChange = (info: any) => {
    if (info.file.status === 'removed') {
      // setSelectedFile(null); // This line is removed
      return;
    }
    // const file = info.file.originFileObj; // This line is removed
    // setSelectedFile(file); // This line is removed
    // setUploadProgress(0); // This line is removed
    // setUploadStatus('none'); // This line is removed
    // setUploadMessage(''); // This line is removed
    console.log('File selected:', info.file);
    // Uncomment below to auto-upload on file select:
    // setTimeout(() => startTusUpload(), 0);
  };

  // --- Inline Tus upload ---
  const startTusUploadInline = async (task: Task, file: File) => {
    setUploadingTaskId(task._id);
    setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'pending' }));
    setUploadProgressMap((prev) => ({ ...prev, [task._id]: 0 }));
    const token = localStorage.getItem('token');
    const upload = new TusUpload(file, {
      endpoint: '/api/upload/tus',
      headers: { Authorization: `Bearer ${token}` },
      metadata: {
        filename: file.name,
        taskId: task._id,
        username: user?.username || '',
        team: user?.team || '',
        category: task.category || '',
        originalFolderName: file.name.replace(/\.[^/.]+$/, ''),
      },
      onError: (error: Error) => {
        setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'failed' }));
        message.error('Upload failed: ' + error.message);
        setUploadingTaskId(null);
        fetchTasks();
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        setUploadProgressMap((prev) => ({ ...prev, [task._id]: Math.floor((bytesUploaded / bytesTotal) * 100) }));
      },
      onSuccess: () => {
        setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'completed' }));
        message.success('Upload completed!');
        setUploadingTaskId(null);
        fetchTasks();
      },
    });
    upload.start();
  };

  // --- Inline Stream upload ---
  const handleStreamUploadInline = async (task: Task, file: File) => {
    setUploadingTaskId(task._id);
    setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'pending' }));
    setUploadProgressMap((prev) => ({ ...prev, [task._id]: 0 }));
    const token = localStorage.getItem('token');
    const url = `/api/tasks/upload-stream?taskId=${task._id}&filename=${encodeURIComponent(file.name)}`;
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgressMap((prev) => ({ ...prev, [task._id]: Math.floor((event.loaded / event.total) * 100) }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'completed' }));
          message.success('Stream upload completed!');
          setUploadingTaskId(null);
          fetchTasks();
        } else {
          setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'failed' }));
          message.error('Stream upload failed!');
          setUploadingTaskId(null);
        }
      };
      xhr.onerror = () => {
        setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'failed' }));
        message.error('Stream upload failed!');
        setUploadingTaskId(null);
      };
      xhr.send(file);
    } catch (err) {
      setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'failed' }));
      message.error('Stream upload failed!');
      setUploadingTaskId(null);
    }
  };

  // --- Inline Tus upload for Re-solved ---
  const startTusUploadReSolved = async (task: Task, file: File) => {
    setUploadingTaskId(task._id);
    setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'pending' }));
    setUploadProgressMap((prev) => ({ ...prev, [task._id]: 0 }));
    const token = localStorage.getItem('token');
    const upload = new TusUpload(file, {
      endpoint: '/api/upload/tus',
      headers: { Authorization: `Bearer ${token}` },
      metadata: {
        filename: file.name,
        taskId: task._id,
        username: user?.username || '',
        team: user?.team || '',
        category: task.category || '',
        originalFolderName: file.name.replace(/\.[^/.]+$/, ''),
        isReSolved: 'true', // Mark as Re-solved upload
      },
      onError: (error: Error) => {
        setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'failed' }));
        message.error('Upload failed: ' + error.message);
        setUploadingTaskId(null);
        fetchTasks();
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        setUploadProgressMap((prev) => ({ ...prev, [task._id]: Math.floor((bytesUploaded / bytesTotal) * 100) }));
      },
      onSuccess: () => {
        setUploadStatusMap((prev) => ({ ...prev, [task._id]: 'completed' }));
        message.success('Upload completed!');
        setUploadingTaskId(null);
        fetchTasks();
      },
    });
    upload.start();
  };

  // --- Handle file input change for native input ---
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUploadTask) return;
    // Frontend file type validation
    const allowedExtensions = ['.tar', '.zip'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      message.error('Invalid file type. Only .tar and .zip files are allowed.');
      e.target.value = '';
      setCurrentUploadTask(null);
      return;
    }
    if (uploadType === 'tus') {
      startTusUploadInline(currentUploadTask, file);
    } else if (uploadType === 're-solved') {
      startTusUploadReSolved(currentUploadTask, file);
    } else {
      handleStreamUploadInline(currentUploadTask, file);
    }
    // Reset input value để có thể chọn lại cùng 1 file nếu muốn
    e.target.value = '';
    setCurrentUploadTask(null);
  };

  // --- Trigger file input ---
  const triggerFileInput = (task: Task, type: 'tus'|'stream'|'re-solved') => {
    setCurrentUploadTask(task);
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const handleFolderUpload = (task: Task) => {
    setSelectedTask(task);
    setUploadModalVisible(true);
  };

  const handleUploadComplete = () => {
    setUploadModalVisible(false);
    setSelectedTask(null);
    fetchTasks(); // Refresh task list
  };

  /**
   * Get table columns configuration based on role.
   * - Admin/Leader: includes interactive Priority Switch.
   * - Employee: shows Priority as icon only.
   */
  const getColumns = () => {
    const baseColumns = [
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        render: (category: string) => (
            <Tag color="blue">{category}</Tag>
        ),
      },
      {
        title: 'Task Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Team',
        dataIndex: 'team',
        key: 'team',
        render: (team: string) => team ? <Tag color="geekblue">{team}</Tag> : <span style={{ color: '#aaa' }}>-</span>,
      },
      {
        title: 'Assigned To',
        dataIndex: 'assignedTo',
        key: 'assignedTo',
      },
      {
        title: 'Creation Date',
        dataIndex: 'creationDate',
        key: 'creationDate',
        render: (date: string) => new Date(date).toLocaleDateString(),
      },
    ];

    const priorityColumn = isEmployee
      ? {
          title: 'Priority',
          dataIndex: 'isPriority',
          key: 'isPriority',
          align: 'center' as const,
          render: (isPriority: boolean) =>
            isPriority ? (
              <Tooltip title="Priority Task">
                <StarFilled style={{ color: '#faad14', fontSize: 18 }} />
              </Tooltip>
            ) : (
              <Tooltip title="Ordinary Task">
                <StarOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />
              </Tooltip>
            ),
        }
      : {
          title: 'Priority',
          dataIndex: 'isPriority',
          key: 'isPriority',
          align: 'center' as const,
          render: (isPriority: boolean, record: Task) => (
            <Switch
              checked={!!isPriority}
              checkedChildren={<StarFilled style={{ color: '#faad14' }} />}
              unCheckedChildren={<StarOutlined style={{ color: '#d9d9d9' }} />}
              onChange={(checked) => handlePriorityToggle(record._id, checked)}
            />
          ),
        };

    // Employee: add status toggles and upload buttons
    if (isEmployee) {
      const isDoneColumn = {
        title: 'Done',
        key: 'isDone',
        align: 'center' as const,
        render: (_: any, record: Task) => (
          <Switch
            checked={!!record.isDone}
            checkedChildren="Done"
            unCheckedChildren="Not Done"
            onChange={(checked) => handleToggleIsDone(record, checked)}
            style={{ backgroundColor: record.isDone ? '#52c41a' : undefined }}
            disabled
          />
        ),
      };
      // --- Re-solved Upload Count column ---
      const reSolvedCountColumn = {
        title: 'Fixed Uploads',
        key: 'reSolvedUploadCount',
        align: 'center' as const,
        render: (_: any, record: Task) => {
          const count = record.reSolvedUploadCount || 0;
          let color = undefined;
          if (count === 1) color = 'warning';
          else if (count === 2) color = 'orange';
          else if (count > 2) color = 'red';
          return (
            <Tag color={color} style={{ fontWeight: 600 }}>
              {count}
            </Tag>
          );
        },
      };
      // --- Upload buttons column (restored, independent from toggles) ---
      const uploadColumn = {
        title: 'Upload',
        key: 'upload',
        align: 'center' as const,
        render: (_: any, record: Task) => {
          const progress = uploadProgressMap[record._id] || 0;
          const status = uploadStatusMap[record._id] || 'none';
          return (
            <Space>
              <Button
                size="small"
                icon={<FolderOpenOutlined />}
                onClick={() => handleFolderUpload(record)}
                disabled={status === 'pending'}
              >
                Upload Folder
              </Button>
              {status === 'pending' && <Progress percent={progress} size="small" style={{ width: 80 }} />}
              {status === 'completed' && <CheckCircleTwoTone twoToneColor="#52c41a" />}
              {status === 'failed' && <CloseCircleTwoTone twoToneColor="#ff4d4f" />}
            </Space>
          );
        },
      };
      // Download/actions column (existing logic, unchanged)
      const actionsColumn = {
        title: 'Download',
        key: 'download',
        render: (_: any, task: Task) => {
          const status = downloadManager.downloadStatusMap[task._id];
          const progress = downloadManager.downloadProgressMap[task._id] ?? 0;
          
          // Check if task is downloaded (either from DB or current session)
          const isDownloaded = task.isDownloaded || status === 'downloaded';
          
          if (isDownloaded) {
            return (
              <Space>
                <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">Downloaded</Tag>
                <Tooltip title="Re-download file">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<RedoOutlined />} 
                    onClick={() => downloadManager.reDownload(task._id, task.name, task.dropboxDownloadLink)}
                    style={{ color: '#1890ff' }}
                  />
                </Tooltip>
              </Space>
            );
          }
          
          if (status === 'downloading' || status === 'processing' || status === 'starting') {
            return <Progress percent={progress} size="small" status={status === 'processing' ? 'active' : undefined} />;
          }
          if (status === 'error') {
            return <Button icon={<ReloadOutlined />} onClick={() => downloadManager.retryDownload(task._id, task.name, task.dropboxDownloadLink)}>Retry</Button>;
          }
          
          // Only show Download button if not downloaded
          return <Button icon={<DownloadOutlined />} onClick={() => downloadManager.startDownload(task._id, task.name, task.dropboxDownloadLink)}>Download</Button>;
        }
      };
      return [...baseColumns, priorityColumn, isDoneColumn, reSolvedCountColumn, uploadColumn, actionsColumn];
    }

    // Admin/Leader: show non-editable status and pickedTime
    const isDoneColumn = {
      title: 'Done',
      key: 'isDone',
      align: 'center' as const,
      render: (_: any, record: Task) => (
        record.isDone ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#d9d9d9" />
      ),
    };
    // --- Re-solved Upload Count column for all roles ---
    const reSolvedCountColumn = {
      title: 'Fixed Uploads',
      key: 'reSolvedUploadCount',
      align: 'center' as const,
      render: (_: any, record: Task) => {
        const count = record.reSolvedUploadCount || 0;
        let color = undefined;
        if (count === 1) color = 'warning';
        else if (count === 2) color = 'orange';
        else if (count > 2) color = 'red';
        return (
          <Tag color={color} style={{ fontWeight: 600 }}>
            {count}
          </Tag>
        );
      },
    };
    const isReSolvedColumn = {
      title: 'Re-solved',
      key: 'isReSolved',
      align: 'center' as const,
      render: (_: any, record: Task) => (
        record.isReSolved ? <CheckCircleTwoTone twoToneColor="#722ed1" /> : <CloseCircleTwoTone twoToneColor="#d9d9d9" />
      ),
    };
    const pickedTimeColumn = {
      title: 'Picked Time',
      key: 'pickedTime',
      align: 'center' as const,
      render: (_: any, record: Task) => (
        record.pickedTime ? new Date(record.pickedTime).toLocaleString() : <span style={{ color: '#aaa' }}>-</span>
      ),
    };
    
    // Delete column for Admin/Leader
    const deleteColumn = {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: Task) => (
        <Button
          type="primary"
          danger
          size="small"
          onClick={() => handleDeleteTask(record._id, record.name)}
        >
          Delete
        </Button>
      ),
    };
    
    return [...baseColumns, priorityColumn, isDoneColumn, reSolvedCountColumn, pickedTimeColumn, deleteColumn];
  };

  // Modal/overlay for screen lock during packaging or download
  const showLock = !!downloadManager.activeDownload &&
    ['processing', 'downloading', 'starting'].includes(downloadManager.activeDownload.phase);
  const lockPhase = downloadManager.activeDownload?.phase;
  const lockTaskId = downloadManager.activeDownload?.taskId;
  const lockTaskName = downloadManager.activeDownload?.taskName;
  const lockProgress = lockTaskId ? downloadManager.downloadProgressMap[lockTaskId] ?? 0 : 0;

  // Debug logging for modal state
  console.log(`🔍 Modal Debug:`, {
    showLock,
    lockPhase,
    lockTaskId,
    lockTaskName,
    activeDownload: downloadManager.activeDownload
  });

  return (
    <>
      {/* Screen lock modal for packaging/download */}
      <Modal
        open={showLock}
        closable={false}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              if (lockTaskId) {
                downloadManager.resetTaskDownload(lockTaskId);
              }
            }}
            danger
          >
            Cancel Download
          </Button>
        ]}
        centered
        maskClosable={false}
        keyboard={false}
        zIndex={2000}
        bodyStyle={{ textAlign: 'center', padding: 32 }}
      >
        {lockPhase === 'processing' && (
          <>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
            <div style={{ marginTop: 16, fontSize: 18 }}>Packaging file for download...</div>
            <Progress percent={lockProgress} size="default" style={{ marginTop: 16 }} />
            <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
              This may take a few minutes for large files
            </div>
          </>
        )}
        {lockPhase === 'downloading' && (
          <>
            <DownloadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            <div style={{ marginTop: 16, fontSize: 18 }}>Downloading file...</div>
            <Progress percent={lockProgress} size="default" style={{ marginTop: 16 }} />
            {lockProgress === 100 && (
              <div style={{ marginTop: 8, fontSize: 14, color: '#52c41a' }}>
                Download completed! Browser dialog will appear shortly...
              </div>
            )}
          </>
        )}
        {lockPhase === 'starting' && (
          <>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
            <div style={{ marginTop: 16, fontSize: 18 }}>Preparing download...</div>
          </>
        )}

      </Modal>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <Title level={isMobile ? 3 : 2} style={{ fontSize: isMobile ? '20px' : '24px' }}>
          <FileTextOutlined style={{ marginRight: '8px' }} />
          {isMobile ? 'Tasks' : 'Task Management'}
        </Title>

        {/* Task Creation Section - Only visible to Admin and Leader */}
        {canCreateTasks && (
          <Card 
            title={
              <Space wrap>
                <FolderOpenOutlined />
                {isMobile ? 'Create Tasks' : 'Create Tasks from Configured Folder'}
              </Space>
            }
            style={{ marginBottom: isMobile ? '16px' : '24px' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                {/*<Button */}
                {/*  type="primary" */}
                {/*  loading={creatingTasks}*/}
                {/*  icon={<PlusOutlined />}*/}
                {/*  onClick={handleCreateTasks}*/}
                {/*  size="large"*/}
                {/*>*/}
                {/*  Create Tasks*/}
                {/*</Button>*/}
                {/*<Button */}
                {/*  type="default" */}
                {/*  loading={importingExcel}*/}
                {/*  icon={<ImportOutlined />}*/}
                {/*  onClick={() => excelFileInputRef.current?.click()}*/}
                {/*  size="large"*/}
                {/*>*/}
                {/*  Import from Excel*/}
                {/*</Button>*/}
                {/*<Button */}
                {/*  type="default" */}
                {/*  icon={<FileExcelOutlined />}*/}
                {/*  onClick={handleDownloadTemplate}*/}
                {/*  size="large"*/}
                {/*>*/}
                {/*  Download Template*/}
                {/*</Button>*/}
                <Button type="primary"
                  icon={<FileExcelOutlined />}
                  onClick={() => setImportGoogleSheetVisible(true)}
                  size="large"
                >
                  Import from Google Sheets
                </Button>
                <input
                  ref={excelFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleExcelFileChange}
                />
              </Space>
              {/*<Alert*/}
              {/*  message="Automatic Task Creation"*/}
              {/*  description={*/}
              {/*    <div>*/}
              {/*      <p>Click "Create Tasks" to automatically create tasks from the configured Input folder.</p>*/}
              {/*      <p>The system will:</p>*/}
              {/*      <ul>*/}
              {/*        <li>Use the pre-configured Input folder path</li>*/}
              {/*        <li>Scan for folder structures following the pattern: <Text code>Category/Product</Text></li>*/}
              {/*        <li>Create one task for each product folder found</li>*/}
              {/*        <li>Skip same-day duplicates automatically</li>*/}
              {/*        <li>Allow cross-day duplicates to create new tasks</li>*/}
              {/*      </ul>*/}
              {/*    </div>*/}
              {/*  }*/}
              {/*  type="info"*/}
              {/*  showIcon*/}
              {/*/>*/}
            </Space>
          </Card>
        )}

        {/* Employee: Pick New Task Button */}
        {isEmployee && (
          <Card style={{ marginBottom: '24px' }}>
            <Tooltip title={hasIncompleteTask ? 'Please complete your current task before picking a new one' : ''}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={pickingTask}
              onClick={handlePickTask}
                disabled={pickingTask || hasIncompleteTask}
              size={isMobile ? 'middle' : 'large'}
              block={isMobile}
            >
              {pickingTask ? 'Picking Task...' : 'Pick New Task'}
            </Button>
            </Tooltip>
          </Card>
        )}

        {/* Task List Section */}
        <Card 
          title={
            <Space>
              <FileTextOutlined />
              {isEmployee ? 'My Assigned Tasks' : 'Task List'}
              {/* Show unassigned count for Admin and Leader */}
              {(user?.role === 'Admin' || user?.role === 'Leader') && (
                <Tag color="orange" style={{ marginLeft: '8px' }}>
                  {unassignedCount} Unassigned
                </Tag>
              )}
              {/* Show total task count for Employee */}
              {isEmployee && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {pagination.total} Total Tasks
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchTasks(pagination.current, pagination.pageSize)}
              loading={loading}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '' : 'Refresh'}
            </Button>
          }
        >
          <Table
            columns={getColumns()}
            dataSource={tasks}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: !isMobile, // Hide on mobile
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} tasks`,
              // For employees, show fewer page size options since they typically have fewer tasks
              pageSizeOptions: isEmployee ? ['5', '10', '20'] : ['10', '20', '50', '100'],
              defaultPageSize: isEmployee ? 10 : 10,
              size: isMobile ? 'small' : 'default',
            }}
            loading={loading}
            onChange={(pagination) => 
              handleTableChange(pagination.current || 1, pagination.pageSize || 10)
            }
            scroll={{ 
              x: isMobile ? 'max-content' : 800,
              y: isMobile ? 'calc(100vh - 400px)' : undefined
            }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Card>

        {/* Information Notice */}
        {!canCreateTasks && !isEmployee && (
          <Alert
            message="Access Information"
            description="You can view tasks but only Admin and Leader roles can create new tasks from the configured folder."
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
        {/* Hidden file input for upload */}
        <input
          type="file"
          accept=".tar,.zip"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
        {/* Folder Upload Modal */}
        <Modal
          title="Upload Folder"
          open={uploadModalVisible}
          onCancel={() => setUploadModalVisible(false)}
          footer={null}
          width={600}
        >
          {selectedTask && (
            <FolderUpload
              taskId={selectedTask._id}
              onComplete={handleUploadComplete}
              isDone={selectedTask.isDone} // Pass isDone to FolderUpload
            />
          )}
        </Modal>

        {/* Google Sheets Import Modal */}
        <Modal
          title="Import from Google Sheets"
          open={importGoogleSheetVisible}
          onOk={handleImportGoogleSheet}
          onCancel={() => {
            setImportGoogleSheetVisible(false);
            setGoogleSheetName('');
          }}
          confirmLoading={importingGoogleSheet}
          okText="Import"
          cancelText="Cancel"
          width={600}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="How to use"
              description={
                <div>
                  <p>1. Google Sheet ID is configured in backend (GOOGLE_SHEET_ID environment variable)</p>
                  <p>2. Make sure your Google Sheet is public (Share → "Anyone with the link can view")</p>
                  <p>3. Sheet format uses fixed column positions:</p>
                  <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Column A: Name</li>
                    <li>Column B: Category</li>
                    <li>Column C: Team</li>
                    <li>Column D: Source download URL</li>
                    <li>Column E: IsPriority (optional)</li>
                  </ul>
                  <p style={{ marginTop: '8px', marginBottom: '0' }}>Note: Column positions are fixed, header names are for reference only.</p>
                </div>
              }
              type="info"
              showIcon
            />
            <div>
              <Text strong>Sheet Name (Optional):</Text>
              <input
                type="text"
                value={googleSheetName}
                onChange={(e) => setGoogleSheetName(e.target.value)}
                placeholder="e.g., Sheet1 (leave empty for first sheet)"
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9'
                }}
              />
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Specify sheet name if your spreadsheet has multiple sheets. Leave empty to use the first sheet.
              </Text>
            </div>
          </Space>
        </Modal>
      </div>
    </>
  );
};

export default TaskTab; 